import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/shared";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

const GREEN = "#35A787";
const ZONES = ["Pune City", "Hinjewadi", "Baner", "Balewadi", "Aundh", "Koregaon Park", "Kothrud", "Wakad", "Viman Nagar", "Kalyani Nagar"];

export default function DriverSignup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", phone: "", password: "", zone: "Pune City" });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!/^\d{10}$/.test(f.phone)) return setErr("Enter a valid 10-digit mobile number");
    if (f.password.length < 4) return setErr("Password must be at least 4 characters");
    setBusy(true);
    const r = await register("driver", f);
    setBusy(false);
    if (r.ok) navigate("/partner/today");
    else setErr(r.error);
  };

  const input = "w-full rounded-lg border border-[#D8D6CF] bg-white px-3 py-2.5 text-sm text-[#1F2937] outline-none transition-colors placeholder:text-[#9CA3AF] focus:border-[#35A787] focus:ring-2 focus:ring-[#35A787]/20";
  const label = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#5B5A56]";

  return (
    <div className="min-h-screen bg-[#FBFAF7]">
      <header className="flex h-16 items-center justify-between border-b border-[#EAE9E4] bg-white px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo size={26} className="max-w-[130px] object-contain" />
          <span className="font-display text-base font-bold tracking-tight text-[#14130E]">Anand</span>
        </Link>
        <Link to="/signup/advertiser" className="text-sm font-medium text-[#35A787] transition-colors hover:text-[#2D8F74]">I want to advertise →</Link>
      </header>

      <div className="mx-auto max-w-md px-4 py-10 sm:py-14">
        <Link to="/" className="mb-6 inline-flex items-center gap-1.5 text-xs font-medium text-[#8A887F] transition-colors hover:text-[#14130E]"><ArrowLeft size={14} /> Back to home</Link>
        <h1 className="font-display text-2xl font-bold tracking-tight text-[#14130E]">Join the cab team</h1>
        <p className="mt-1.5 text-sm text-[#6B6659]">Create your driver account now. As soon as a cab is assigned in your zone, you start earning.</p>

        <form onSubmit={submit} className="mt-6 space-y-4 rounded-2xl border border-[#EAE9E4] bg-white p-6 shadow-[0_1px_3px_rgba(16,24,40,0.06)]">
          <div>
            <label className={label}>Full name</label>
            <input className={input} value={f.name} onChange={set("name")} placeholder="e.g. Raju Kumar" required />
          </div>
          <div>
            <label className={label}>Mobile number</label>
            <input className={input} type="tel" value={f.phone} onChange={set("phone")} placeholder="10-digit mobile" pattern="[0-9]{10}" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Area / zone</label>
              <select className={input} value={f.zone} onChange={set("zone")}>
                {ZONES.map((z) => <option key={z}>{z}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Password</label>
              <input className={input} type="password" value={f.password} onChange={set("password")} placeholder="Min 4 characters" minLength={4} required />
            </div>
          </div>

          {err && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">{err}</div>}

          <button type="submit" disabled={busy} className="tap flex w-full items-center justify-center gap-2 rounded-xl bg-[#35A787] px-5 py-3.5 text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-60">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Creating account…</> : "Join the waitlist"}
          </button>
          <p className="text-center text-[11px] leading-relaxed text-[#8A887F]">
            You'll be matched to a cab as slots free up in your zone. Nothing to pay — ever.
          </p>
        </form>

        <ul className="mt-5 space-y-2 text-sm text-[#4B5563]">
          <li className="flex items-start gap-2"><CheckCircle2 size={16} className="mt-0.5 flex-none text-[#35A787]" /> Your partner account is live immediately</li>
          <li className="flex items-start gap-2"><CheckCircle2 size={16} className="mt-0.5 flex-none text-[#35A787]" /> Start tracking your waitlist status in the app</li>
        </ul>
      </div>
    </div>
  );
}